import { NavLink } from 'react-router-dom';
import { darkTheme } from '../lib/tokens';
import { canViewPath, useAdminRole } from '../features/auth/permissions';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Übersicht',
    items: [{ to: '/', label: 'Home', end: true }],
  },
  {
    title: 'Nutzer & Feedback',
    items: [
      { to: '/users', label: 'Benutzer-Verwaltung' },
      { to: '/feedback', label: 'Feedback-Inbox', end: true },
      { to: '/feedback/auswertung', label: 'Feedback-Auswertung' },
    ],
  },
  {
    title: 'Inhalte',
    items: [{ to: '/articles', label: 'Artikel (Blog / Wissen)' }],
  },
  {
    title: 'Kommunikation',
    items: [
      { to: '/announcements', label: 'Announcements' },
      { to: '/broadcasts', label: 'Push-Broadcasts' },
    ],
  },
  {
    // Der Drilldown `/social/post/:id` steht bewusst NICHT in der Navigation —
    // man kommt aus dem Kalender oder aus dem Bestand dorthin, nie direkt.
    title: 'Social',
    items: [
      { to: '/social', label: 'Kalender', end: true },
      { to: '/social/week', label: 'Woche planen' },
      { to: '/social/assets', label: 'Bestand' },
      { to: '/social/review/new', label: 'Review-Beitrag' },
      { to: '/social/rhythmen', label: 'Rhythmen' },
    ],
  },
  {
    // Doku der Content- und Audio-Strecke fuer Technologie-Gespraeche
    // (CUR-1571). Steht NICHT in VIEWER_PATHS — damit ADMIN-only.
    title: 'Technologie',
    items: [{ to: '/pipelines', label: 'Pipelines' }],
  },
  {
    title: 'Plattform',
    items: [
      { to: '/health', label: 'System-Health' },
      { to: '/services', label: 'Services & Cache' },
      { to: '/audit', label: 'Audit-Log' },
    ],
  },
  {
    title: 'Analytics',
    items: [{ to: '/analytics', label: 'Eigene KPIs' }, { to: '/ai-conversations', label: 'KI-Gespräche' }],
  },
];

/**
 * Findet das Label des aktiven Nav-Eintrags fuer einen Pfad — fuer die mobile
 * Titelzeile. Exakter Treffer fuer `end`-Eintraege (Home), sonst Prefix-Match
 * (z.B. `/users/123` -> "Benutzer-Verwaltung"). Faellt auf "Übersicht" zurueck.
 */
export function findNavLabel(pathname: string): string {
  for (const group of NAV_GROUPS) {
    for (const item of group.items) {
      if (item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`)) {
        return item.label;
      }
    }
  }
  return 'Übersicht';
}

/**
 * Die Navigations-Gruppen + Links. Wird von der Desktop-Sidebar (≥ lg) und vom
 * mobilen Drawer (< lg) gemeinsam gerendert. `onNavigate` wird beim Klick auf
 * einen Link gefeuert (Drawer schliesst sich dann).
 *
 * Gefiltert wird mit derselben Funktion, die auch die `ProtectedRoute`
 * benutzt (CUR-995) — es gibt bewusst keine zweite Liste, die auseinanderlaufen
 * koennte. Eine Gruppe ohne sichtbare Eintraege verschwindet mitsamt
 * Ueberschrift.
 */
export function NavList({ onNavigate }: { onNavigate?: () => void }): JSX.Element {
  const role = useAdminRole();
  const groups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.filter((item) => canViewPath(role, item.to)),
  })).filter((group) => group.items.length > 0);

  return (
    <nav className="flex-1 overflow-y-auto px-3 py-2">
      {groups.map((group) => (
        <div key={group.title} className="mb-4">
          <div className="px-2 py-1 text-[10px] font-semibold uppercase tracking-widest text-text-tertiary">
            {group.title}
          </div>
          {group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={onNavigate}
              className={({ isActive }) =>
                [
                  // min-h-[44px] gibt mobil ein Daumen-taugliches Touch-Target;
                  // lg:min-h-0 setzt die Desktop-Sidebar exakt auf den alten Wert
                  // zurueck (Sidebar ist nur ≥ lg sichtbar, Drawer nur < lg).
                  'relative flex min-h-[44px] items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors lg:min-h-0',
                  isActive
                    ? 'bg-accent/10 text-accent-light font-semibold'
                    : 'text-text-secondary hover:bg-bg-elevated hover:text-text-primary',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      aria-hidden
                      className="absolute left-0 top-1.5 bottom-1.5 w-[2.5px] rounded-sm"
                      style={{ background: darkTheme.colors.accent }}
                    />
                  )}
                  {item.label}
                </>
              )}
            </NavLink>
          ))}
        </div>
      ))}
    </nav>
  );
}
